import React, { useState } from "react";

// One club crest, turned toward its rival for the showdown.
//
// Three rendering rules this component exists to enforce:
//
//  1. TRANSPARENCY. Crest art is a transparent PNG/SVG, so it gets NO wrapper
//     background and NO `shadow-*` utility — a box-shadow is drawn from the
//     element's rectangle and shows up as a grey plate behind the transparent
//     corners. Depth comes from `filter: drop-shadow()`, which follows the
//     artwork's own alpha edge instead.
//  2. NEVER MIRROR. No scaleX/scaleY anywhere, so a crest's lettering can't come
//     out reversed. `turn` is a rotation about the vertical axis, which fore-
//     shortens one edge — it does not flip anything.
//  3. UPRIGHT, NEVER LEANING. Client Rev 5: "Keep both shields vertically
//     upright. Do not rotate or lean either shield sideways. Instead, apply a
//     subtle inward 3D perspective so the shields appear to face one another."
//     There is deliberately no `rotate()` here any more — that was the sideways
//     lean the client asked us to take out. `perspective(...) rotateY(...)`
//     turns the crest in depth while every edge stays vertical.
//
// The crest is lazy + async-decoded: it is a decorative brand asset, so it must
// never hold up first paint. If none of the club's sources resolve, a neutral
// shield in the club's accent colour is drawn inline (real <text>, so the
// abbreviation cannot be flipped either) and nothing shows a broken image.
// `nudge` slides the crest toward the centre line so the pair overlap behind the
// VS badge. It lives in the same inline transform as the turn on purpose: an
// inline `transform` replaces the property outright, so a Tailwind
// `translate-x-*` class here would be silently dropped.
//
// `turn` is degrees of rotateY. Positive brings the crest's LEFT edge forward
// and pushes its RIGHT edge back (measured in the browser, not assumed), so the
// left-hand crest takes a positive value and the right-hand one its negation —
// which is what makes the pair face each other.
const ClubShield = ({
  club,
  turn = 0,
  perspective = 300,
  nudge = 0,
  className = "w-[76px] h-[92px] md:w-[96px] md:h-[116px]",
}) => {
  const [sourceIndex, setSourceIndex] = useState(0);
  const src = club.sources?.[sourceIndex];

  return (
    <div
      className={`shrink-0 ${className}`}
      style={{
        // `perspective()` goes inside the transform, not on a parent: the two
        // crests sit in different grid/flex cells, and a shared parent
        // perspective would give them a single vanishing point off to one side
        // — the far crest would turn much harder than the near one. Per-element,
        // each crest is viewed head-on and turns by exactly the same amount.
        transform: `perspective(${perspective}px) translateX(${nudge}px) rotateY(${turn}deg)`,
        // Centre, not "bottom center": the pivot for a Y rotation is the
        // vertical mid-line, and anchoring it to the baseline makes the crest
        // appear to swing rather than simply turn.
        transformOrigin: "center",
        filter: "drop-shadow(0 6px 10px rgba(15, 23, 42, 0.16))",
      }}
    >
      {src ? (
        <img
          src={src}
          alt={`${club.name} crest`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain bg-transparent"
          onError={() => setSourceIndex((i) => i + 1)}
        />
      ) : (
        <FallbackShield club={club} />
      )}
    </div>
  );
};

const FallbackShield = ({ club }) => (
  <svg viewBox="0 0 64 78" className="w-full h-full" role="img" aria-label={`${club.name} crest`}>
    <path
      d="M32 2 6 9.5v28.9C6 55.6 17.3 68.6 32 74c14.7-5.4 26-18.4 26-35.6V9.5L32 2Z"
      fill={club.accent}
    />
    <path
      d="M32 8.6 12 14.3v24.1C12 51.9 20.6 62.4 32 67c11.4-4.6 20-15.1 20-28.6V14.3L32 8.6Z"
      fill="none"
      stroke="#FFFFFF"
      strokeOpacity="0.55"
      strokeWidth="1.6"
    />
    <text
      x="32"
      y="44"
      fill="#FFFFFF"
      fontSize={club.abbr.length > 2 ? 15 : 18}
      fontWeight="700"
      textAnchor="middle"
      fontFamily="Montserrat, Poppins, sans-serif"
      letterSpacing="0.5"
    >
      {club.abbr}
    </text>
  </svg>
);

export default ClubShield;
